/**
 * 🚨 CMT Issues Banner
 * 
 * Displays a banner when users have CMT assignment issues
 * (deleted CMTs, missing assignments, etc.) but are still allowed platform access.
 */

import React, { useState } from 'react';
import { 
  Alert, 
  Button, 
  Space, 
  Typography, 
  Collapse, 
  Tag, 
  List,
  Modal
} from 'antd';
import { 
  ExclamationCircleOutlined, 
  TeamOutlined,
  ContactsOutlined,
  InfoCircleOutlined,
  CloseOutlined,
  WarningOutlined
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

const CMTIssuesBanner = ({ cmtIssues, onDismiss, onContactAdmin }) => {
  const [visible, setVisible] = useState(true);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);

  if (!visible || !cmtIssues) return null;

  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  const getAlertType = () => {
    switch (cmtIssues.reason) {
      case 'NO_CMT_ASSIGNMENTS':
        return 'info';
      case 'ALL_CMTS_MISSING':
        return 'error';
      case 'SOME_CMTS_MISSING':
        return 'warning';
      case 'CMT_VALIDATION_ERROR':
        return 'warning';
      default:
        return 'warning';
    }
  };

  const getIcon = () => {
    switch (cmtIssues.reason) {
      case 'NO_CMT_ASSIGNMENTS':
        return <InfoCircleOutlined />;
      case 'ALL_CMTS_MISSING':
        return <WarningOutlined />;
      case 'SOME_CMTS_MISSING':
        return <ExclamationCircleOutlined />;
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  const getTitle = () => {
    switch (cmtIssues.reason) {
      case 'NO_CMT_ASSIGNMENTS':
        return 'No Crisis Management Team Assignments';
      case 'ALL_CMTS_MISSING':
        return 'All Assigned Teams No Longer Exist';
      case 'SOME_CMTS_MISSING':
        return 'Some Assigned Teams No Longer Exist';
      case 'CMT_VALIDATION_ERROR':
        return 'Unable to Verify Team Assignments';
      default:
        return 'Crisis Management Team Issues';
    }
  };

  const renderDetailsModal = () => (
    <Modal
      title="Crisis Management Team Assignment Details"
      open={detailsModalVisible}
      onCancel={() => setDetailsModalVisible(false)}
      footer={[
        <Button key="contact" type="primary" onClick={onContactAdmin} icon={<ContactsOutlined />}>
          Contact Administrator
        </Button>,
        <Button key="close" onClick={() => setDetailsModalVisible(false)}>
          Close
        </Button>
      ]}
      width={700}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Issue Summary */}
        <Alert
          type={getAlertType()}
          message={getTitle()}
          description={cmtIssues.message}
          showIcon
        />

        {/* Active CMTs */}
        {cmtIssues.activeCMTs && cmtIssues.activeCMTs.length > 0 && (
          <div>
            <Text strong>✅ Active Team Assignments:</Text>
            <List
              size="small"
              bordered
              dataSource={cmtIssues.activeCMTs}
              renderItem={cmt => (
                <List.Item>
                  <Space>
                    <TeamOutlined style={{ color: '#52c41a' }} />
                    <Text>{cmt.cmtData?.name || 'Unnamed CMT'}</Text>
                    <Tag color="green">{cmt.cmtRole}</Tag>
                  </Space>
                </List.Item>
              )}
            />
          </div>
        )}

        {/* Missing CMTs */}
        {cmtIssues.missingCMTs && cmtIssues.missingCMTs.length > 0 && (
          <div>
            <Text strong>❌ Missing Team Assignments:</Text>
            <List
              size="small"
              bordered
              dataSource={cmtIssues.missingCMTs}
              renderItem={cmt => (
                <List.Item>
                  <Space>
                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                    <Text delete>CMT ID: {cmt.cmtId}</Text>
                    <Tag color="red">{cmt.cmtRole}</Tag>
                    <Text type="secondary">
                      Assigned: {cmt.assignedAt ? new Date(cmt.assignedAt).toLocaleDateString() : 'Unknown'}
                    </Text>
                  </Space>
                </List.Item>
              )}
            />
          </div>
        )}

        {/* Impact Information */}
        <Collapse size="small">
          <Panel header="What does this mean?" key="impact">
            <Space direction="vertical">
              <Paragraph>
                <Text strong>Current Impact:</Text>
              </Paragraph>
              <ul>
                <li>You can still access the platform and view existing content</li>
                <li>Some team-specific features may not work properly</li>
                <li>You may not receive team notifications</li>
                <li>Incident management capabilities may be limited</li>
              </ul>
              
              <Paragraph>
                <Text strong>To resolve this issue:</Text>
              </Paragraph>
              <ul>
                <li>Contact your organization administrator</li>
                <li>Request reassignment to active Crisis Management Teams</li>
                <li>Verify your role and responsibilities within the organization</li>
              </ul>
            </Space>
          </Panel>
        </Collapse>
      </Space>
    </Modal>
  );

  return (
    <>
      <Alert
        type={getAlertType()}
        message={getTitle()}
        description={
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>{cmtIssues.message}</Text>
            <Space wrap>
              <Button 
                size="small" 
                onClick={() => setDetailsModalVisible(true)}
                icon={<InfoCircleOutlined />}
              >
                View Details
              </Button>
              <Button 
                size="small" 
                type="primary"
                onClick={onContactAdmin}
                icon={<ContactsOutlined />}
              >
                Contact Administrator
              </Button>
            </Space>
          </Space>
        }
        icon={getIcon()}
        closable
        onClose={handleDismiss}
        showIcon
        style={{ 
          marginBottom: 16,
          border: `1px solid ${getAlertType() === 'error' ? '#ff4d4f' : getAlertType() === 'warning' ? '#faad14' : '#1890ff'}`
        }}
        action={
          <Button 
            size="small" 
            icon={<CloseOutlined />} 
            onClick={handleDismiss}
            type="text"
          />
        }
      />

      {renderDetailsModal()}
    </>
  );
};

export default CMTIssuesBanner;
